/**
 * One package in the catalogue grid.
 *
 * Deliberately a card and not a pill row. Paragraph 15 of the design notes puts
 * a LIST of clickable objects on pills - a client, an invoice, a ticket, things
 * you scan down and open. A package is read sideways against its neighbours
 * instead: price against price, what is included against what is included, and
 * that comparison needs the three lines of body copy a 56px pill has nowhere to
 * put.
 */
import { Pencil, Star, Trash2 } from "lucide-react";
import {
  ServiceIcon,
  formatMoney,
  localeText,
  serviceKind,
  type Locale,
  type PlanDTO,
  type PlansT,
  type ProductDTO,
} from "@wa/ui";
import { Btn, Card, IconBtn, StatusPill } from "../../../ui";

export function PlanCard({
  plan,
  product,
  showProduct,
  locale,
  t,
  onEdit,
  onDelete,
}: {
  plan: PlanDTO;
  product?: ProductDTO;
  /** Name the service on the card - only worth it when the grid mixes several. */
  showProduct?: boolean;
  locale: Locale;
  t: PlansT;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const kind = serviceKind(product, plan.productCode);
  const isPlatform = kind === "platform";
  const enabledCount = Object.values(plan.features).filter(
    (v) => v === true || (typeof v === "number" && v !== 0),
  ).length;
  // A display-only package carries no entitlements, so the honest count is how
  // many promises it makes rather than a permanent zero.
  const highlightCount = plan.highlights
    ? (plan.highlights[locale] ?? plan.highlights.et ?? plan.highlights.en ?? []).length
    : 0;
  const count = isPlatform ? enabledCount : highlightCount;
  const countLabel = isPlatform ? t("packages.features") : t("packages.highlights");
  const badgeText = localeText(plan.badge, locale);
  const free = plan.priceMonthlyCents === 0 && plan.priceYearlyCents === 0;

  return (
    // `.crm-card` is only a surface - background, radius, shadow - so the body
    // brings its own padding. `mt-auto` on the action row needs the column, and
    // the grid stretches every card in a row to the tallest one, so the buttons
    // sit on one line across the row however long the descriptions run.
    <Card className="flex flex-col px-6 py-5">
      {showProduct && product ? (
        <span className="crm-mut mb-2 flex min-w-0 items-center gap-1.5 text-[11.5px] font-semibold">
          <ServiceIcon name={product.icon} className="size-3.5 shrink-0" />
          <span className="crm-trunc">{localeText(product.name, locale)}</span>
        </span>
      ) : null}

      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="crm-trunc text-[15.5px] font-bold tracking-[-0.022em]">
            {localeText(plan.name, locale)}
          </div>
          <code className="crm-mut text-[11.5px]">{plan.code}</code>
        </div>
        {/* The editor can mark a package as the featured card on the public
            pricing page. The shared card never showed it, so the switch was
            write-only: you could set it and then never tell which package had it. */}
        {plan.featured ? (
          <Star className="size-4 shrink-0 fill-[var(--color-primary)] text-[var(--color-primary)]" aria-hidden />
        ) : null}
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <StatusPill tone={plan.isPublic ? "ok" : "mut"}>
          {plan.isPublic ? t("packages.public") : t("packages.hidden")}
        </StatusPill>
        {badgeText ? <span className="crm-chip">{badgeText}</span> : null}
      </div>

      <div className="mt-3.5">
        {free ? (
          <div className="crm-tnum text-[26px] font-bold tracking-[-0.03em]">{t("packages.free")}</div>
        ) : (
          <div className="flex flex-wrap items-baseline gap-1.5">
            <span className="crm-tnum text-[26px] font-bold tracking-[-0.03em]">
              {formatMoney(plan.priceMonthlyCents, plan.currency, locale)}
            </span>
            <span className="crm-mut text-[12.5px]">{t("packages.perMonth")}</span>
          </div>
        )}
        {!free && plan.priceYearlyCents > 0 ? (
          <div className="crm-mut crm-tnum mt-0.5 text-[11.75px]">
            {formatMoney(plan.priceYearlyCents, plan.currency, locale)} {t("packages.perYear")}
          </div>
        ) : null}
      </div>

      {plan.description ? (
        <p className="crm-mut mt-2.5 line-clamp-2 text-[12.5px] leading-[1.5]">
          {localeText(plan.description, locale)}
        </p>
      ) : null}

      <div className="crm-mut mt-3 flex flex-wrap items-center gap-x-1.5 text-[11.75px]">
        <span className="crm-tnum font-bold text-[var(--color-foreground)]">{count}</span>
        <span>{countLabel}</span>
        <span aria-hidden>·</span>
        {/* A Veebihaldus package is never a subscription: what uses it are the
            watched sites on it. Unknown (the CRM database did not answer) reads "-". */}
        <span className="crm-tnum font-bold text-[var(--color-foreground)]">
          {kind === "monitoring" ? (plan.siteCount ?? "-") : plan.subscriberCount}
        </span>
        <span>{kind === "monitoring" ? t("packages.sites") : t("packages.subscribers")}</span>
      </div>

      <div className="mt-auto flex gap-2 pt-4">
        <Btn small className="flex-1 justify-center" onClick={onEdit}>
          <Pencil className="size-3.5" />
          {t("edit")}
        </Btn>
        <IconBtn small onClick={onDelete} aria-label={t("delete")} title={t("delete")}>
          <Trash2 className="size-4" />
        </IconBtn>
      </div>
    </Card>
  );
}
